import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import useStyles from './styles';
import { PageHeader, SubmissionCard } from 'molecules';
import { Submission, SubmissionStatus } from 'types/submissions';

interface MySubmissionsProps {
  submissions: Submission[];
}

type FilterValue = SubmissionStatus | 'Flagged' | 'All';

const filters: { label: string; value: SubmissionStatus | 'Flagged' | 'All' }[] = [
  { label: 'All', value: 'All' },
  { label: 'Pending', value: 'Review' },
  { label: 'Action', value: 'Flagged' },
  { label: 'Approved', value: 'Approved' },
  { label: 'Published', value: 'Completed' },
  { label: 'Declined', value: 'Rejected' },
];

/**
 * Align with `SubmissionCard` display: `createdAt` is usually a numeric timestamp string.
 * Avoid `new Date(digitsOnlyString)` — use `parseInt` then `new Date(number)` like `formatDate(parseInt(...))`.
 */
function submissionCreatedAtMs(createdAt: string): number {
  const s = String(createdAt).trim();
  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    if (!Number.isFinite(n)) return NaN;
    const ms = new Date(n).getTime();
    return Number.isFinite(ms) ? ms : NaN;
  }
  const ms = new Date(s).getTime();
  return Number.isFinite(ms) ? ms : NaN;
}

/**
 * Sort by parsed `createdAt` when both resolve; otherwise by `orderNumber` so the
 * sort toggle still changes order when timestamps tie or are missing.
 */
function compareSubmissions(a: Submission, b: Submission, newestFirst: boolean): number {
  const ta = submissionCreatedAtMs(a.createdAt);
  const tb = submissionCreatedAtMs(b.createdAt);
  const aValid = Number.isFinite(ta);
  const bValid = Number.isFinite(tb);

  if (aValid && bValid && ta !== tb) {
    return newestFirst ? tb - ta : ta - tb;
  }
  if (aValid !== bValid) {
    return aValid ? -1 : 1;
  }

  const orderCmp = a.orderNumber.localeCompare(b.orderNumber, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
  return newestFirst ? -orderCmp : orderCmp;
}

/**
 * MySubmissions Component
 *
 * Displays the user’s submissions in a single chronological list (`createdAt`),
 * with optional status filtering and a toggle for newest-first vs oldest-first.
 *
 * Features:
 * - PageHeader title; toolbar below with status filter (left) and sort toggle (right)
 * - Sort order toggle (latest ↔ oldest)
 * - Empty message when no submissions match the filter
 *
 * Used in:
 * - MySubmissions layout index (`data-layout` on the index shell; `withLayout` `md` width)
 *
 * @param props - Component props
 * @param props.submissions - Array of submission objects to display
 */
const MySubmissions = ({ submissions }: MySubmissionsProps): React.JSX.Element => {
  const { classes } = useStyles();
  const [filter, setFilter] = useState<FilterValue>('All');
  const [newestFirst, setNewestFirst] = useState(true);

  const filteredSubmissions = useMemo(
    () =>
      filter === 'All'
        ? submissions
        : submissions.filter(({ status, flagged }) =>
            filter === 'Flagged' ? flagged : status === filter,
          ),
    [filter, submissions],
  );

  const sortedSubmissions = useMemo(
    () => [...filteredSubmissions].sort((a, b) => compareSubmissions(a, b, newestFirst)),
    [filteredSubmissions, newestFirst],
  );

  const handleFilter = (event: { target: { value: unknown } }) => {
    setFilter(event.target.value as FilterValue);
  };

  const valToLabel = (value: FilterValue): string | undefined => {
    const filterOption = filters.find((filterObject) => filterObject.value === value);
    return filterOption?.label;
  };

  const filterControl = (
    <FormControl variant="filled" size="small" className={classes.filter}>
      <InputLabel id="submission-status-filter-label" shrink>
        Status
      </InputLabel>
      <Select
        labelId="submission-status-filter-label"
        value={filter}
        onChange={handleFilter}
        variant="filled"
        size="small"
        disableUnderline
        label="Status"
      >
        {filters.map(({ value, label }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box className={classes.container}>
      <PageHeader title="MY SUBMISSIONS" divider />

      <Stack spacing={2} width="100%">
        <Box className={classes.toolbarRow}>
          {filterControl}
          {sortedSubmissions.length > 0 && (
            <Button
              type="button"
              variant="text"
              color="primary"
              size="small"
              className={classes.sortToggle}
              onClick={() => setNewestFirst((prev) => !prev)}
              startIcon={
                newestFirst ? (
                  <KeyboardArrowDownIcon fontSize="small" aria-hidden />
                ) : (
                  <KeyboardArrowUpIcon fontSize="small" aria-hidden />
                )
              }
              aria-label={
                newestFirst
                  ? 'Sorted latest first. Activate to sort oldest first.'
                  : 'Sorted oldest first. Activate to sort latest first.'
              }
            >
              Latest
            </Button>
          )}
        </Box>

        {sortedSubmissions.map((item) => (
          <SubmissionCard key={item.orderNumber} submission={item} />
        ))}

        {sortedSubmissions.length === 0 && (
          <Typography className={classes.empty} component="p" variant="body1">
            You have no submissions of {valToLabel(filter) || 'this'} status.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default MySubmissions;
